import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getReport } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import moment from "moment";
import excel from "../../components/excel/excel";
import Table from "../../components/advancedTable";
import "./reporting.css";

export default function Report() {
    const dispatch = useDispatch();
    const salesReport = useSelector((state) => state.order.order);
    const currentUser = useSelector((state) => state.user.currentUser);
    const isFetching = useSelector((state) => state.order.isFetching);

    const TwoDecimalFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const superadmin = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "superadmin"
            : null
    );

    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [modalData, setModalData] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    //eslint-disable-next-line
    const [depositAmount, setDepositAmount] = useState(0);
    const [commissionAmount, setCommissionAmount] = useState(0);
    const [profitAmount, setProfitAmount] = useState(0);
    const [inputs, setInputs] = useState({
        principle: undefined,
        createdBy: undefined,
        dateFrom: moment(Date.now()).startOf("month").format("YYYY-MM-DD"),
        dateTo: moment(Date.now()).add(1, "days").format("YYYY-MM-DD"),
    });

    const getAdmin = async () => {
        const res =
            process.env.REACT_APP_COUNTRY === "SINGAPORE"
                ? await publicRequest.get("/users/admin", {
                      params: { isAdmin: true },
                  })
                : await publicRequest.get("/users/superadmin", {
                      params: { isAdmin: true },
                  });
        setUsersData(res.data);
    };

    const handleDateFrom = (e) => {
        setInputs((prev) => {
            return { ...prev, dateFrom: e.target.value };
        });
    };
    const handleDateTo = (e) => {
        setInputs((prev) => {
            return { ...prev, dateTo: e.target.value };
        });
    };

    const [usersData, setUsersData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const res = await getReport(dispatch, inputs);
            setTotalAmount(0);
            setDepositAmount(0);
            setCommissionAmount(0);
            setProfitAmount(0);
            setModalData(res);
            setReportData(res || []);
            if (res) {
                setIsLoaded(true);
                for (const item of res) {
                    if (item.outlet === "HAOFA Tourbillon Singapore") {
                    } else if (item.outlet !== "HAOFA Tourbillon Singapore") {
                        setTotalAmount((prev) => prev + item.monthlySales);
                        setDepositAmount((prev) => prev + item.monthlyDeposit);
                        setCommissionAmount((prev) => prev + item.commission);
                        setProfitAmount((prev) => prev + item.profit);
                    }
                }
            }
        };
        fetchData();
    }, [dispatch, inputs]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getAdmin();
        }
    }, [usersData]);

    const columns = [
        {
            accessor: "outlet",
            Header: "Dealer",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding font-medium">{value || "-"}</div>
            ),
        },
        {
            accessor: "salesAmount",
            Header: "Total Units",
            sortable: false,
            Cell: ({ value }) => (
                <div className="initialPadding">
                    <span className="inline-flex px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                        {value || 0} units
                    </span>
                </div>
            ),
        },
        {
            accessor: "totalSales",
            Header: "Total Sales",
            sortable: false,
            Cell: (params) => (
                <div className="initialPadding">
                    <span className="text-lg font-bold text-green-600">
                        {params.row.original.totalSales
                            ? `${
                                  process.env.REACT_APP_COUNTRY ===
                                      "SINGAPORE" ||
                                  params.row.original.outlet ===
                                      "HAOFA Tourbillon Singapore"
                                      ? "$"
                                      : "RM"
                              } ${TwoDecimalFormatter.format(
                                  params.row.original.totalSales
                              )}`
                            : `${
                                  process.env.REACT_APP_COUNTRY ===
                                      "SINGAPORE" ||
                                  params.row.original.outlet ===
                                      "HAOFA Tourbillon Singapore"
                                      ? "$"
                                      : "RM"
                              } ${TwoDecimalFormatter.format(0)}`}
                    </span>
                </div>
            ),
        },
    ];

    return (
        <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
            <div className="productList bg-white rounded-3xl shadow-2xl p-8 m-6 border border-slate-200/50">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-slate-800 mb-2">
                        Sales Reporting
                    </h1>
                    <p className="text-slate-600">
                        Comprehensive sales analysis and dealer performance
                        metrics
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 tracking-wide">
                            From Date
                        </label>
                        <div className="luxury-input-wrapper">
                            <input
                                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 font-medium text-slate-700"
                                name="dateFrom"
                                value={inputs.dateFrom}
                                type="date"
                                onChange={(e) => handleDateFrom(e)}
                                style={{
                                    background:
                                        "linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))",
                                    border: "2px solid rgb(226 232 240)",
                                    borderRadius: "12px",
                                    padding: "12px 16px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                }}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700 tracking-wide">
                            To Date
                        </label>
                        <div className="luxury-input-wrapper">
                            <input
                                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300 font-medium text-slate-700"
                                name="dateTo"
                                value={inputs.dateTo}
                                type="date"
                                onChange={(e) => handleDateTo(e)}
                                style={{
                                    background:
                                        "linear-gradient(to right, rgb(248 250 252), rgb(239 246 255))",
                                    border: "2px solid rgb(226 232 240)",
                                    borderRadius: "12px",
                                    padding: "12px 16px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                excel({
                                    excelData: salesReport,
                                    fileName: `Haofa-Sales-${moment(
                                        inputs.dateFrom
                                    ).format("DD-MM-YYYY")}-to-${moment(
                                        inputs.dateTo
                                    ).format("DD-MM-YYYY")}`,
                                });
                            }}
                            className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-800 font-bold rounded-xl shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center gap-3 border border-amber-300"
                        >
                            <span className="text-slate-700 text-lg">📊</span>
                            <span className="tracking-wide">Export Excel</span>
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-2xl p-8 shadow-2xl border border-green-500/30 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-400/20 to-transparent rounded-full -mr-16 -mt-16"></div>
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-green-100 text-lg font-semibold tracking-wide">
                                    Total Sales
                                </span>
                                <div className="w-12 h-12 bg-green-500/30 rounded-xl flex items-center justify-center">
                                    <span className="text-2xl">💰</span>
                                </div>
                            </div>
                            <div className="text-4xl font-bold text-white mb-2">
                                {process.env.REACT_APP_COUNTRY === "SINGAPORE"
                                    ? "$"
                                    : "RM"}{" "}
                                {TwoDecimalFormatter.format(totalAmount)}
                            </div>
                            <div className="text-green-200 text-sm">
                                Total Revenue Generated
                            </div>
                        </div>
                    </div>

                    {superadmin && (
                        <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 rounded-2xl p-8 shadow-2xl border border-purple-500/30 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-400/20 to-transparent rounded-full -mr-16 -mt-16"></div>
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-purple-100 text-lg font-semibold tracking-wide">
                                        Total Profit
                                    </span>
                                    <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center">
                                        <span className="text-2xl">📈</span>
                                    </div>
                                </div>
                                <div className="text-4xl font-bold text-white mb-2">
                                    {process.env.REACT_APP_COUNTRY ===
                                    "SINGAPORE"
                                        ? "$"
                                        : "RM"}{" "}
                                    {TwoDecimalFormatter.format(profitAmount)}
                                </div>
                                <div className="text-purple-200 text-sm">
                                    Net Profit Margin
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {isLoaded && (
                    <Table
                        data={isFetching === false ? reportData : []}
                        columns={columns}
                        pageCount={Math.ceil(reportData.length / 100)}
                        totalCount={reportData.length}
                        updatePagination={() => {}}
                        paginationState={{ page: 1, limit: 100 }}
                        updateLoaded={() => {}}
                        hidePagination={reportData.length <= 100}
                    />
                )}
            </div>
        </div>
    );
}
